import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { useMutation } from '@/composables/api'
import { authProviders } from '@/composables/useAuth'
import { ApiError } from '@/utils/api'
import type { components } from '@/types/_api'

export type LoginMethod = components['schemas']['Authenticate-LoginMethodsResponse']['methods'][number]
export type LoginProvider = (typeof authProviders)[number]

export interface LoginMethodsView {
  /** One OAuth provider → that one; several → all of them, equally sized; none → the one-time code. Never empty. */
  primary: LoginProvider[]
  /** Shown under "other sign-in options"; an empty list hides the link. */
  others: LoginProvider[]
}

// The one-time-code entry, kept as a list so it can serve as a view slice as-is.
const otpProviders = authProviders.filter((provider) => provider.id === 'email')

/**
 * Splits the methods an address may use into the primary button(s) and the collapsed rest.
 * Method ids without an entry in `authProviders` are ignored; the result never has an empty primary.
 */
export function toLoginMethodsView(methods: readonly string[]): LoginMethodsView {
  const known = authProviders.filter((provider) => methods.includes(provider.id))
  const oauth = known.filter((provider) => provider.id !== 'email')

  // No known OAuth method reads exactly like an account that only has the one-time code.
  if (oauth.length === 0) return { primary: otpProviders, others: [] }

  return { primary: oauth, others: known.filter((provider) => provider.id === 'email') }
}

/** Only technical failures reach the user; a valid address always gets a view. */
function toErrorKey(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.statusCode === 400) return 'common.email_login_invalid_email'
    if (err.statusCode === 429) return 'common.too_many_attempts'
  }
  return 'common.email_login_unknown_error'
}

/**
 * Looks up which login methods an e-mail address may use in this application.
 * `lookup` resolves to a view for every 200 and to `null` only on a technical error, described by `errorKey`.
 */
export function useLoginMethods(): {
  lookup: (email: string) => Promise<LoginMethodsView | null>
  loading: ComputedRef<boolean>
  errorKey: Ref<string | null>
} {
  const mutation = useMutation('post', '/authenticates/login-methods', {
    meta: { notifyOnError: false, notifyOnSuccess: false },
  })
  const errorKey = ref<string | null>(null)

  async function lookup(email: string): Promise<LoginMethodsView | null> {
    errorKey.value = null
    try {
      const response = await mutation.mutateAsync({
        // @ts-expect-error Swagger lies
        body: { application: import.meta.env.VITE_APP_NAME, email },
      })
      return toLoginMethodsView(response.methods)
    } catch (err) {
      errorKey.value = toErrorKey(err)
      return null
    }
  }

  return {
    lookup,
    loading: computed(() => mutation.isPending.value),
    errorKey,
  }
}
